import { LitElement, css, html } from '@lit'
import { t } from '@nova/utils'
import * as paths from '@nova/paths'

class NovaSearchForm extends LitElement {
  static properties = {
    url: { type: String },
    value: { type: String },
    placeholder: { type: String },
    label: { type: String },
    noResults: { type: String },
    searchAgain: { type: String },
    openState: { type: String },
  }

  constructor() {
    super()

    this.emptySearchData = {
      resources: {
        results: {
          products: [],
          collections: []
        }
      }
    }
    this.searchData = {}
    this.searchTerm = ''
    this.searchOnChange = this.searchOnChange.bind(this)
    this.doSearchOnChange = this.doSearchOnChange.bind(this)
  }

  firstUpdated(changedProperties) {
    super.firstUpdated(changedProperties)
    const searchInput = this.shadowRoot.querySelector('.header-search__input')
    searchInput.addEventListener('focus', this.doSearchOnChange)
    searchInput.addEventListener('input', this.searchOnChange)
    searchInput.addEventListener('keydown', this.searchOnChange)
  }

  doSearchOnChange(event) {
    this.openState = 'opened'
    this.searchOnChange(event)
  }

  searchOnChange(event) {
    const keyCode = event.keyCode || event.which
    if (keyCode === 27) {
      this.openState = 'closed'
      document.documentElement.classList.remove('sl-scroll-lock')
      return
    }

    if (this.openState === 'closed') return

    const searchValue = event.target?.value || ''
    this.value = searchValue
    clearTimeout(this.debounceTimeout)
    this.debounceTimeout = setTimeout(() => {
      this.getSearchResults(searchValue)
    }, 300)
  }

  async getSearchResults(searchTerm) {
    if (searchTerm.length < 3) {
      this.searchData = this.emptySearchData
      this.searchTerm = ''

      this.requestUpdate()
      return
    }

    try {
      const response = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(
          searchTerm,
        )}&resources[type]=product,collection`,
      )
      if (!response.ok) {
        console.error(`HTTP error! Status: ${response.status}`)
        this.searchData = this.emptySearchData
        this.searchTerm = ''
      }
      const data = await response.json()
      this.searchData = data
      this.searchTerm = searchTerm
    } catch (error) {
      console.error('Error:', error.message)
      this.searchData = this.emptySearchData
      this.searchTerm = ''
    }

    this.requestUpdate()
  }

  render() {
    return html`
      <form
        class="header-search__form"
        action=${this.url}
        method="get"
        role="search"
      >
        <div class="field header-search__field">
          <sl-input
            class="header-search__input h4"
            id="Search-In-Modal"
            type="search"
            name="q"
            value=${this.value}
            placeholder=${t('general.search.search')}
            label="Press 'Enter' to search"
            autocomplete="off"
          >
            <div name="search" slot="suffix">
              ${paths.search}
            </div>
          </sl-input>
          <input type="hidden" name="options[prefix]" value="last" />
          <slot name="close-button"></slot>
        </div>
      </form>
      <nova-search-result
        .searchData=${this.searchData}
        .searchTerm=${this.searchTerm}
        openState=${this.openState}
        noResults=${t('general.search.no_results_found_for')}
        searchAgain=${t('general.search.try_again')}
      ></nova-search-result>
    `
  }

  static styles = css`
    :host {
      width: 100%;
      overflow: auto;
      -ms-overflow-style: none; 
      scrollbar-width: none; 
    }

    :host::-webkit-scrollbar {
      display: none;
      width: 0;
    }

    :host .header-search__form {
      padding: var(--nova-space-xs) var(--nova-space-page-margin);
      background: var(--nova-color-white);
    }

    @media screen and (min-width: 1024px) {
      :host .header-search__form {
        padding: 0;
      }
    }

    :host nova-search-result {
      display: block;
      padding-inline: var(--nova-space-page-margin);
      background-color: var(--nova-color-grey-100);
    }

    :host .header-search__field {
      display: flex;
      align-items: center;
    }

    :host .header-search__input {
      width: 100%;
      border: none;
      font: var(--nova-h4-font);
    }

    :host .visually-hidden {
      position: absolute !important;
      overflow: hidden;
      width: 1px;
      height: 1px;
      margin: -1px;
      padding: 0;
      border: 0;
      clip: rect(0 0 0 0);
      word-wrap: normal !important;
    }

    :host .header-search__label {
      display: block;
      padding: var(--nova-space-sm) 0 var(--nova-space-md);
      color: var(--nova-color-subdued);
    }

    :host .header-search__input::part(base) {
      background-color: var(--nova-color-grey-100);
      border-width: 0;
      border-radius: var(--nova-border-radius-xs);
      box-shadow: none;
      height: 38px;
    }

    :host .header-search__input::part(form-control-label) {
      display: none;
    }

    :host .header-search__input::part(input) {
      font: var(--nova-body-font);
    }

    @media screen and (max-width: 767px) {
      :host .header-search__input::part(input) {
        font-size: 16px !important;
      }
    }

    :host .header-search__input::part(input)::placeholder {
      color: var(--nova-color-grey-500);
      opacity: 1; /* Firefox */
    }

    :host .header-search__input::part(input)::-ms-input-placeholder { /* Edge 12 -18 */
      color: var(--nova-color-grey-500);
    }

    @media screen and (max-width: 767px) {
      :host .header-search__input::part(input)::placeholder {
        font-size: 14px;
      }

      :host .header-search__input::part(input)::-ms-input-placeholder { /* Edge 12 -18 */
        font-size: 14px;
      }
    }

    :host .header-search__input:focus-visible {
      outline: none;
    }

    /* Clear default clear button from Internet Explorer */
    :host input[type='search']::-ms-clear {
      display: none;
      -webkit-appearance: none;
      width: 0;
      height: 0;
    }

    /* Clear default clear button from Chrome */
    :host input[type='search']::-webkit-search-cancel-button {
      display: none;
      -webkit-appearance: none;
    }

    ::slotted([slot='search-icon']) {
      padding-right: var(--nova-space-xs);
    }

    @media screen and (min-width: 768px) {
      ::slotted([slot='search-icon']) {
        display: none;
      }
    }

    :host .header-search__input [slot="suffix"] {
      display: flex;
      margin-inline-end: var(--nova-space-sm);
    }

    @media screen and (min-width: 1024px) {
      :host .header-search__input [slot="suffix"] {
        margin-inline-end: var(--nova-space-xsm);
      }
    }
  `
}

customElements.define('nova-search-form', NovaSearchForm)
